import type { CartItem } from '../context/CartContext';
import { fetchQueuedAccountWrite, getAccountWriteQueue } from '../queuedAccountWrites';
import type {
  Order,
  OrderItem,
} from '../context/OrderHistoryContext';
import {
  normalizeFabricBlendLabel,
  normalizeColorHex,
  normalizeFitLabel,
  normalizeGenderLabel,
  normalizeMaterialRecord,
  normalizeMeasurementsRecord,
  normalizeSizeValue,
} from '../features/cartItemConfiguration';
import { isAllowedCountryCode, normalizeCountryCode } from '../features/addressOptions';
import { normalizeDeliveryEvents } from '../features/deliveryEvents';

const MULTIPLE_SPACES_REGEX = /\s+/g;
const DISALLOWED_HTML_CHARS_REGEX = /[<>]/g;
const SAFE_HOST_REGEX = /^[a-z0-9.-]+(?::\d+)?$/i;
const CLIENT_ENV: Partial<ImportMetaEnv> =
  (import.meta as ImportMeta & { env?: Partial<ImportMetaEnv> }).env ?? {};

export const AUTH_FLAG_KEY = 'vadauro_authed';
export const AUTH_TOKEN_KEY = 'authToken';
export const AUTH_USER_KEY = 'authUser';
export const ORDER_STORAGE_KEY = 'vadauro_orders';
export const CART_STORAGE_KEY = 'vadauro_cart';
export const AUTH_STATE_CHANGE_EVENT = 'vadauro:auth-state-change';
export const AI_OUTFIT_SELECTION_STORAGE_KEY = 'vadauro_ai_outfit_selection';
export const AI_OUTFIT_SELECTION_CHANGE_EVENT = 'vadauro:ai-outfit-selection-change';
export const TRY_ON_PENDING_ADDS_STORAGE_KEY = 'vadauro_try_on_pending_adds';
export const TRY_ON_PENDING_ADDS_CHANGE_EVENT = 'vadauro:try-on-pending-adds-change';
export const STAGED_TRY_ON_ITEMS_STORAGE_KEY = 'vadauro_try_on_staged_items';
export const VIRTUAL_TRY_ON_STATE_RESET_EVENT = 'vadauro:virtual-try-on-state-reset';
export const GUEST_SESSION_COOKIE_NAME = 'guest_session_id';
export const US_SSN_REQUIRED_ORDER_TOTAL = 2500;
const GUEST_SESSION_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

function parsePositiveIntegerEnvExpression(value: string | undefined, fallbackValue: number) {
  if (!value) {
    return fallbackValue;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return fallbackValue;
  }

  if (/^\d+$/.test(trimmedValue)) {
    const parsedValue = Number.parseInt(trimmedValue, 10);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallbackValue;
  }

  if (/^\d+(?:\s*\*\s*\d+)+$/.test(trimmedValue)) {
    const parsedValue = trimmedValue
      .split('*')
      .map((part) => Number.parseInt(part.trim(), 10))
      .reduce((product, part) => product * part, 1);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallbackValue;
  }

  return fallbackValue;
}

export const MAX_IMAGE_UPLOAD_SIZE_BYTES = parsePositiveIntegerEnvExpression(
  CLIENT_ENV.VITE_MAX_IMAGE_UPLOAD_SIZE_BYTES,
  10 * 1024 * 1024
);

function formatImageUploadLimitMessage(maxImageUploadSizeBytes: number) {
  const maxImageUploadSizeMegabytes = (maxImageUploadSizeBytes / (1024 * 1024))
    .toFixed(1)
    .replace(/\.0$/, '');
  return `Images must be ${maxImageUploadSizeMegabytes}MB or smaller.`;
}

type UnknownRecord = Record<string, unknown>;

export interface SanitizedCheckoutItem {
  title: string;
  description: string;
  image: string;
  price: number;
  quantity: number;
}

export interface SanitizedCheckoutCustomer {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  address: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string;
}

interface StorageOptions {
  storage?: 'local' | 'session';
}

function dispatchAuthStateChange() {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
}

function clearVirtualTryOnStateAtAuthBoundary() {
  removeStorageValue(AI_OUTFIT_SELECTION_STORAGE_KEY, { storage: 'local' });
  removeStorageValue(AI_OUTFIT_SELECTION_STORAGE_KEY, { storage: 'session' });
  removeStorageValue(TRY_ON_PENDING_ADDS_STORAGE_KEY, { storage: 'local' });
  removeStorageValue(TRY_ON_PENDING_ADDS_STORAGE_KEY, { storage: 'session' });
  removeStorageValue(STAGED_TRY_ON_ITEMS_STORAGE_KEY, { storage: 'local' });
  removeStorageValue(STAGED_TRY_ON_ITEMS_STORAGE_KEY, { storage: 'session' });

  if (typeof window === 'undefined') {
    return;
  }

  // Always notify listeners so in-memory selections are reset even if storage
  // was unavailable or a prior persistence attempt failed.
  window.dispatchEvent(new Event(AI_OUTFIT_SELECTION_CHANGE_EVENT));
  window.dispatchEvent(new Event(TRY_ON_PENDING_ADDS_CHANGE_EVENT));
  window.dispatchEvent(new Event(VIRTUAL_TRY_ON_STATE_RESET_EVENT));
}

function sanitizeGuestSessionId(value: unknown) {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim().replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 80);
}

function readCookieValue(name: string) {
  if (typeof document === 'undefined') {
    return '';
  }

  const encodedName = `${encodeURIComponent(name)}=`;
  const cookieEntry = document.cookie
    .split(';')
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(encodedName));

  if (!cookieEntry) {
    return '';
  }

  try {
    return decodeURIComponent(cookieEntry.slice(encodedName.length));
  } catch {
    return '';
  }
}

function writeCookieValue(name: string, value: string, maxAgeSeconds = GUEST_SESSION_MAX_AGE_SECONDS) {
  if (typeof document === 'undefined') {
    return;
  }

  const cookieParts = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${Math.max(Math.trunc(maxAgeSeconds), 0)}`,
    'SameSite=Lax',
  ];

  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    cookieParts.push('Secure');
  }

  document.cookie = cookieParts.join('; ');
}

function generateGuestSessionId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return sanitizeGuestSessionId(globalThis.crypto.randomUUID());
  }

  return sanitizeGuestSessionId(`guest${Date.now()}${Math.random().toString(36).slice(2, 12)}`);
}

export function getGuestSessionId() {
  return sanitizeGuestSessionId(readCookieValue(GUEST_SESSION_COOKIE_NAME));
}

export function ensureGuestSessionId() {
  const existingSessionId = getGuestSessionId();
  if (existingSessionId) {
    // Refresh the cookie lifetime while preserving the same stable guest id.
    writeCookieValue(GUEST_SESSION_COOKIE_NAME, existingSessionId);
    return existingSessionId;
  }

  const nextSessionId = generateGuestSessionId();
  if (!nextSessionId) {
    return '';
  }

  writeCookieValue(GUEST_SESSION_COOKIE_NAME, nextSessionId);
  return nextSessionId;
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isSafeHost(value: unknown): value is string {
  return typeof value === 'string' && SAFE_HOST_REGEX.test(value);
}

function normalizeWhitespace(value: string, trimEdges = true) {
  const withoutControlChars = Array.from(value)
    .filter((char) => {
      const codePoint = char.charCodeAt(0);
      return codePoint > 31 && codePoint !== 127;
    })
    .join('');

  const normalizedWhitespace = withoutControlChars.replace(MULTIPLE_SPACES_REGEX, ' ');
  return trimEdges ? normalizedWhitespace.trim() : normalizedWhitespace;
}

function sanitizeText(
  value: string,
  {
    maxLength,
    allowedPattern,
    removeHtmlChars = true,
    trimEdges = true,
  }: { maxLength: number; allowedPattern?: RegExp; removeHtmlChars?: boolean; trimEdges?: boolean }
) {
  let nextValue = normalizeWhitespace(value, trimEdges);

  if (removeHtmlChars) {
    nextValue = nextValue.replace(DISALLOWED_HTML_CHARS_REGEX, '');
  }

  if (allowedPattern) {
    nextValue = Array.from(nextValue)
      .filter((char) => allowedPattern.test(char))
      .join('');
  }

  return nextValue.slice(0, maxLength);
}

export function safeJsonParse<T>(rawValue: string, fallback: T): T {
  try {
    return JSON.parse(rawValue) as T;
  } catch {
    return fallback;
  }
}

export function sanitizeName(value: string) {
  return sanitizeText(value, {
    maxLength: 60,
    allowedPattern: /[a-zA-ZÀ-ÿ' -]/,
  });
}

export function sanitizeEmail(value: string) {
  return value
    .split('')
    .filter((char) => char.charCodeAt(0) > 31 && char.charCodeAt(0) !== 127)
    .join('')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .slice(0, 254);
}

export function sanitizeUsername(value: string) {
  return value
    .split('')
    .filter((char) => char.charCodeAt(0) > 31 && char.charCodeAt(0) !== 127)
    .join('')
    .trim()
    .replace(/[^a-zA-Z0-9._-]/g, '')
    .slice(0, 32);
}

export function sanitizeLoginIdentifier(value: string) {
  return value
    .split('')
    .filter((char) => char.charCodeAt(0) > 31 && char.charCodeAt(0) !== 127)
    .join('')
    .trim()
    .replace(/\s+/g, '')
    .replace(DISALLOWED_HTML_CHARS_REGEX, '')
    .slice(0, 254);
}

export function sanitizePassword(value: string) {
  return value
    .split('')
    .filter((char) => char.charCodeAt(0) > 31 && char.charCodeAt(0) !== 127)
    .join('')
    .slice(0, 128);
}

export function sanitizePhone(value: string) {
  return sanitizeText(value, {
    maxLength: 24,
    allowedPattern: /[0-9+() -]/,
  });
}

export function sanitizeCompany(value: string) {
  return sanitizeText(value, {
    maxLength: 80,
    allowedPattern: /[a-zA-ZÀ-ÿ0-9&.,'()/ -]/,
  });
}

export function sanitizeAddress(value: string) {
  return sanitizeText(value, {
    maxLength: 160,
    allowedPattern: /[\p{L}\p{M}\p{N}#.,'"&()/ -]/u,
  });
}

export function sanitizeAddressInput(value: string) {
  return sanitizeText(value, {
    maxLength: 160,
    allowedPattern: /[\p{L}\p{M}\p{N}#.,'"&()/ -]/u,
    trimEdges: false,
  });
}

export function sanitizeCity(value: string) {
  return sanitizeText(value, {
    maxLength: 80,
    allowedPattern: /[\p{L}\p{M}.' -]/u,
  });
}

export function sanitizeCityInput(value: string) {
  return sanitizeText(value, {
    maxLength: 80,
    allowedPattern: /[\p{L}\p{M}.' -]/u,
    trimEdges: false,
  });
}

export function sanitizeStateProvince(value: string) {
  return sanitizeText(value, {
    maxLength: 80,
    allowedPattern: /[\p{L}\p{M}\p{N}.' -]/u,
  });
}

export function sanitizePostalCode(value: string) {
  return sanitizeText(value, {
    maxLength: 20,
    allowedPattern: /[\p{L}\p{M}\p{N} -]/u,
  }).toUpperCase();
}

export function sanitizeCountry(value: string) {
  return sanitizeText(value, {
    maxLength: 80,
    allowedPattern: /[\p{L}\p{M}.'() -]/u,
  });
}

export function sanitizeGenericText(value: string, maxLength = 160) {
  return sanitizeText(value, { maxLength });
}

export function sanitizeSearchInput(value: string, maxLength = 80) {
  return sanitizeText(value, {
    maxLength,
  });
}

export function sanitizeHeightInput(value: string) {
  return value.replace(/[^\d]/g, '').slice(0, 3);
}

export function sanitizeSsn(value: string) {
  return value.replace(/[^\d]/g, '').slice(0, 9);
}

export function extractSsnLast4(value: unknown) {
  const digits = sanitizeSsn(
    typeof value === 'string' || typeof value === 'number' ? String(value) : '',
  );

  return digits.length >= 4 ? digits.slice(-4) : '';
}

export function sanitizeMeasurementInput(value: string) {
  return value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1').slice(0, 6);
}

export function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function isStrongPassword(value: string) {
  return (
    value.length >= 8 &&
    /[A-Z]/.test(value) &&
    /[a-z]/.test(value) &&
    /\d/.test(value)
  );
}

export function sanitizeErrorMessage(
  message: unknown,
  fallback = 'Something went wrong. Please try again.'
) {
  if (typeof message !== 'string') {
    return fallback;
  }

  const nextMessage = sanitizeGenericText(message, 180);
  return nextMessage || fallback;
}

export function sanitizeToken(value: unknown) {
  if (typeof value !== 'string') {
    return '';
  }

  const normalizedValue = value.trim().replace(/^Bearer\s+/i, '');
  return normalizedValue.replace(/[^a-zA-Z0-9._-]/g, '').slice(0, 4096);
}

export function getStorage(storage: 'local' | 'session' = 'local') {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return storage === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

export function readStorageValue(key: string, { storage = 'local' }: StorageOptions = {}) {
  const targetStorage = getStorage(storage);

  try {
    return targetStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

// Cache-only entries that are safe to drop when a write hits the storage
// quota. Critical state (auth, selections, sessions) must never be listed —
// evicting these only costs a re-download or a backend re-fetch.
const EVICTABLE_STORAGE_KEY_PREFIXES = [
  'vadauro_try_on_image_data',
  'vadauro_try_on_combination_results',
];

function evictCacheStorageEntries(targetStorage: Storage) {
  const keysToRemove: string[] = [];
  for (let index = 0; index < targetStorage.length; index += 1) {
    const storedKey = targetStorage.key(index);
    if (
      storedKey &&
      EVICTABLE_STORAGE_KEY_PREFIXES.some((prefix) => storedKey.startsWith(prefix))
    ) {
      keysToRemove.push(storedKey);
    }
  }

  keysToRemove.forEach((storedKey) => {
    try {
      targetStorage.removeItem(storedKey);
    } catch {
      // Ignore storage access failures.
    }
  });
  return keysToRemove.length > 0;
}

export function writeStorageValue(
  key: string,
  value: string,
  { storage = 'local' }: StorageOptions = {}
) {
  const targetStorage = getStorage(storage);
  if (!targetStorage) {
    return false;
  }

  try {
    targetStorage.setItem(key, value);
    return true;
  } catch {
    // Likely a quota failure: image caches can fill the whole quota, which
    // would otherwise make every later write (e.g. outfit selection) silently
    // no-op. Drop cache-only entries and retry once.
  }

  try {
    if (!evictCacheStorageEntries(targetStorage)) {
      return false;
    }

    targetStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function listStorageKeys(prefix: string, { storage = 'local' }: StorageOptions = {}) {
  const targetStorage = getStorage(storage);
  if (!targetStorage) {
    return [] as string[];
  }

  const matchedKeys: string[] = [];
  try {
    for (let index = 0; index < targetStorage.length; index += 1) {
      const storedKey = targetStorage.key(index);
      if (storedKey && storedKey.startsWith(prefix)) {
        matchedKeys.push(storedKey);
      }
    }
  } catch {
    // Ignore storage access failures.
  }

  return matchedKeys;
}

export function removeStorageValue(key: string, { storage = 'local' }: StorageOptions = {}) {
  const targetStorage = getStorage(storage);

  try {
    targetStorage?.removeItem(key);
  } catch {
    // Ignore storage access failures.
  }
}

const FAILED_AUTH_ATTEMPTS_KEY_PREFIX = 'auth.failedAttempts.';

// Failed authentication attempts are persisted so that a page refresh does not
// drop the Turnstile requirement that kicks in after repeated failures.
export function readFailedAuthAttempts(scope: string): number {
  const raw = readStorageValue(`${FAILED_AUTH_ATTEMPTS_KEY_PREFIX}${scope}`);
  const parsed = Number.parseInt(raw ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function writeFailedAuthAttempts(scope: string, attempts: number) {
  const key = `${FAILED_AUTH_ATTEMPTS_KEY_PREFIX}${scope}`;
  if (attempts <= 0) {
    removeStorageValue(key);
    return;
  }
  writeStorageValue(key, String(attempts));
}

export function sanitizeRoutePath(value: unknown, fallback = '/measurement') {
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return fallback;
  }

  if (value.startsWith('//')) {
    return fallback;
  }

  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://vadauro.com';
    const parsed = new URL(value, origin);
    return parsed.origin === origin ? `${parsed.pathname}${parsed.search}${parsed.hash}` : fallback;
  } catch {
    return fallback;
  }
}

export function setAuthenticatedSession() {
  clearVirtualTryOnStateAtAuthBoundary();
  writeStorageValue(AUTH_FLAG_KEY, '1', { storage: 'session' });
  removeStorageValue(AUTH_FLAG_KEY, { storage: 'local' });
  dispatchAuthStateChange();
}

export function isAuthenticatedSession() {
  const token = getAuthToken();
  if (token) {
    return true;
  }

  const sessionAuth = readStorageValue(AUTH_FLAG_KEY, { storage: 'session' }) === '1';
  if (sessionAuth) {
    clearAuthenticatedSession();
  }

  const legacyLocalAuth = readStorageValue(AUTH_FLAG_KEY, { storage: 'local' }) === '1';
  if (legacyLocalAuth) {
    clearAuthenticatedSession();
  }

  return false;
}

export function clearAuthenticatedSession() {
  clearVirtualTryOnStateAtAuthBoundary();
  removeStorageValue(AUTH_FLAG_KEY, { storage: 'session' });
  removeStorageValue(AUTH_FLAG_KEY, { storage: 'local' });
  removeStorageValue(AUTH_TOKEN_KEY, { storage: 'session' });
  removeStorageValue(AUTH_TOKEN_KEY, { storage: 'local' });
  removeStorageValue(AUTH_USER_KEY, { storage: 'local' });
  removeStorageValue(AUTH_USER_KEY, { storage: 'session' });
  dispatchAuthStateChange();
}

export function getAuthToken() {
  const localToken = sanitizeToken(readStorageValue(AUTH_TOKEN_KEY, { storage: 'local' }));
  if (localToken) {
    return localToken;
  }

  const legacySessionToken = sanitizeToken(readStorageValue(AUTH_TOKEN_KEY, { storage: 'session' }));
  if (legacySessionToken) {
    writeStorageValue(AUTH_TOKEN_KEY, legacySessionToken, { storage: 'local' });
    removeStorageValue(AUTH_TOKEN_KEY, { storage: 'session' });
    return legacySessionToken;
  }

  return '';
}

export function storeAuthToken(value: unknown) {
  const token = sanitizeToken(value);
  if (!token) {
    removeStorageValue(AUTH_TOKEN_KEY, { storage: 'session' });
    removeStorageValue(AUTH_TOKEN_KEY, { storage: 'local' });
    dispatchAuthStateChange();
    return '';
  }

  writeStorageValue(AUTH_TOKEN_KEY, token, { storage: 'local' });
  removeStorageValue(AUTH_TOKEN_KEY, { storage: 'session' });
  dispatchAuthStateChange();
  return token;
}

export function storeAuthenticatedUser(value: unknown) {
  if (!isRecord(value)) {
    removeStorageValue(AUTH_USER_KEY, { storage: 'local' });
    dispatchAuthStateChange();
    return null;
  }

  try {
    const serializedValue = JSON.stringify(value);
    writeStorageValue(AUTH_USER_KEY, serializedValue, { storage: 'local' });
    dispatchAuthStateChange();
    return value;
  } catch {
    removeStorageValue(AUTH_USER_KEY, { storage: 'local' });
    dispatchAuthStateChange();
    return null;
  }
}

export function getAuthenticatedUser() {
  const rawValue = readStorageValue(AUTH_USER_KEY, { storage: 'local' });
  return parseJsonValue(rawValue);
}

function sanitizeAccountIdentifier(value: unknown) {
  return typeof value === 'string' || typeof value === 'number'
    ? sanitizeGenericText(String(value), 80)
    : '';
}

function extractAccountIdFromRecord(value: UnknownRecord) {
  const nestedAccount = isRecord(value.account) ? value.account : null;

  return (
    sanitizeAccountIdentifier(value.account_id ?? value.accountId ?? value.id) ||
    sanitizeAccountIdentifier(
      nestedAccount?.account_id ??
      nestedAccount?.accountId ??
      nestedAccount?.id
    ) ||
    sanitizeAccountIdentifier(value.sub)
  );
}

export function getAuthenticatedAccountId(user: unknown = getAuthenticatedUser()) {
  if (isRecord(user)) {
    const accountId = extractAccountIdFromRecord(user);
    if (accountId) {
      return accountId;
    }
  }

  const token = getAuthToken();
  const decodedPayload = token ? decodeJwtPayload(token) : null;

  return isRecord(decodedPayload) ? extractAccountIdFromRecord(decodedPayload) : '';
}

export function getAuthenticatedUploadAccountId(user: unknown = getAuthenticatedUser()) {
  if (isRecord(user)) {
    const accountId = extractAccountIdFromRecord(user);
    if (accountId) {
      return accountId;
    }
  }

  return getAuthenticatedAccountId();
}

export function getSessionAccountId(user: unknown = getAuthenticatedUser()) {
  const authenticatedAccountId = getAuthenticatedUploadAccountId(user);
  if (authenticatedAccountId) {
    return authenticatedAccountId;
  }

  return ensureGuestSessionId();
}

function decodeJwtPayload(token: string) {
  const [, payload = ''] = token.split('.');
  if (!payload || typeof window === 'undefined' || typeof window.atob !== 'function') {
    return null;
  }

  try {
    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=');
    const decodedPayload = window.atob(paddedPayload);
    const utf8Payload = decodeURIComponent(
      decodedPayload
        .split('')
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );

    return parseJsonValue(utf8Payload);
  } catch {
    return null;
  }
}

export function isAdminSession() {
  const authenticatedUser = getAuthenticatedUser();

  if (isRecord(authenticatedUser) && authenticatedUser.admin === true) {
    return true;
  }

  const token = getAuthToken();
  const decodedPayload = token ? decodeJwtPayload(token) : null;

  return isRecord(decodedPayload) && decodedPayload.admin === true;
}

export function createAuthorizedHeaders(headers?: HeadersInit) {
  const nextHeaders = new Headers(headers);
  const token = getAuthToken();

  if (token && !nextHeaders.has('Authorization')) {
    nextHeaders.set('Authorization', `Bearer ${token}`);
  }

  return nextHeaders;
}

export function createUserSessionHeaders(
  headers?: HeadersInit,
  options?: { ensureGuestSession?: boolean; guestHeaderName?: string }
) {
  const nextHeaders = createAuthorizedHeaders(headers);

  if (!nextHeaders.has('Authorization')) {
    const guestSessionId = options?.ensureGuestSession ? ensureGuestSessionId() : getGuestSessionId();
    const guestHeaderName = options?.guestHeaderName || 'X-Session-ID';
    if (guestSessionId && !nextHeaders.has(guestHeaderName)) {
      nextHeaders.set(guestHeaderName, guestSessionId);
    }
  }

  return nextHeaders;
}

export function isAuthErrorStatus(status: number) {
  return status === 401 || status === 403;
}

const STORE_SCOPED_PATHS = new Set([
  '/api/verify_email',
  '/api/resend_verification_email',
  '/api/manage_categories',
  '/api/manage_site_settings',
  '/api/add_order',
  '/api/add_items',
  '/api/upload_item_image',
  '/api/manage_notifications',
]);

const STORE_SCOPED_WRITE_PATHS = new Set([
  '/api/add_items',
  '/api/upload_item_image',
]);

function resolveStoreName() {
  return typeof __STORE_NAME__ === 'string' ? __STORE_NAME__.trim() : '';
}

function appendQueryParameter(url: string, key: string, value: string) {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${key}=${encodeURIComponent(value)}`;
}

function injectStoreIntoJsonBody(body: BodyInit | null | undefined, storeName: string) {
  if (typeof body === 'string' && body) {
    try {
      const parsedBody = JSON.parse(body) as unknown;
      if (parsedBody && typeof parsedBody === 'object' && !Array.isArray(parsedBody)) {
        return JSON.stringify({
          ...(parsedBody as Record<string, unknown>),
          store: storeName,
        });
      }
    } catch {
      return body;
    }

    return body;
  }

  if (!body) {
    return JSON.stringify({ store: storeName });
  }

  return body;
}

export function normalizeTuxedoCategoryInJsonBody(body: BodyInit | null | undefined) {
  if (typeof body !== 'string' || !body) {
    return body;
  }

  try {
    const parsedBody = JSON.parse(body) as unknown;
    if (!parsedBody || typeof parsedBody !== 'object' || Array.isArray(parsedBody)) {
      return body;
    }

    const bodyRecord = parsedBody as Record<string, unknown>;
    if (typeof bodyRecord.category !== 'string') {
      return body;
    }

    const normalizedCategory = bodyRecord.category.trim().toLowerCase().replace(/[\s_-]+/g, '');
    if (!['tuxedo', 'tuxedos', 'tuxedo/formalwear', 'formalwear'].includes(normalizedCategory)) {
      return body;
    }

    return JSON.stringify({ ...bodyRecord, category: 'Tuxedo/Formal Wear' });
  } catch {
    return body;
  }
}

export async function fetchAwsApi(path: string, init: RequestInit = {}): Promise<Response> {
  const awsApiUrl = sanitizeApiBaseUrl(CLIENT_ENV.VITE_AWS_API_URL);
  if (!awsApiUrl) {
    throw new Error('API configuration missing. Please set VITE_AWS_API_URL in your .env file.');
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const requestPathName = normalizedPath.split('?')[0];
  const requestMethod = (init.method ?? 'GET').toUpperCase();
  const storeName = resolveStoreName();

  let requestUrl = `${awsApiUrl}${normalizedPath}`;
  let requestBody = init.body;

  if (STORE_SCOPED_PATHS.has(requestPathName) && storeName) {
    if (requestMethod === 'GET' && !(requestPathName === '/api/manage_site_settings' && new URLSearchParams(normalizedPath.split('?')[1] ?? '').has('store'))) {
      requestUrl = appendQueryParameter(requestUrl, 'store', storeName);
    } else if (
      requestMethod === 'POST'
      || requestMethod === 'PATCH'
      || (requestMethod === 'PUT' && requestPathName === '/api/manage_notifications')
      || ((requestMethod === 'PUT' || requestMethod === 'DELETE')
        && STORE_SCOPED_WRITE_PATHS.has(requestPathName))
    ) {
      requestBody = injectStoreIntoJsonBody(requestBody, storeName);
    }
  }

  if (
    requestPathName === '/api/add_items'
    && (requestMethod === 'POST' || requestMethod === 'PUT')
  ) {
    requestBody = normalizeTuxedoCategoryInJsonBody(requestBody);
  }

  const queue = getAccountWriteQueue(normalizedPath, init);
  if (queue) return fetchQueuedAccountWrite(queue, { ...init, body: requestBody }, fetchAwsApi,
    Object.fromEntries(new URLSearchParams(normalizedPath.split('?')[1] ?? '')));

  const response = await fetch(requestUrl, {
    ...init,
    body: requestBody,
    headers: createAuthorizedHeaders(init.headers),
  });

  if (isAuthErrorStatus(response.status)) {
    clearAuthenticatedSession();
  }

  return response;
}

export function sanitizeApiBaseUrl(value: unknown) {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return '';
  }

  try {
    const parsed = new URL(trimmedValue);
    const isLocalhost =
      parsed.hostname === 'localhost' ||
      parsed.hostname === '127.0.0.1' ||
      parsed.hostname.endsWith('.localhost');

    if (parsed.protocol === 'https:' || (isLocalhost && parsed.protocol === 'http:')) {
      const normalizedPath = parsed.pathname.replace(/\/$/, '');
      return `${parsed.origin}${normalizedPath}`;
    }

    return '';
  } catch {
    return '';
  }
}

export function isSafeRedirectUrl(value: unknown, allowedHostSuffixes: string[]) {
  if (typeof value !== 'string') {
    return false;
  }

  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === 'https:' &&
      allowedHostSuffixes.some((suffix) => parsed.hostname === suffix || parsed.hostname.endsWith(`.${suffix}`))
    );
  } catch {
    return false;
  }
}

export function parseJsonValue(rawValue: string | null) {
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue) as unknown;
  } catch {
    return null;
  }
}

function unwrapProxyApiBody(parsed: UnknownRecord) {
  if (!('body' in parsed)) {
    return null;
  }

  const proxyStatusCode = Number(parsed.statusCode);
  const hasValidProxyStatus = Number.isInteger(proxyStatusCode);
  const attachProxyStatusCode = (value: unknown) => {
    if (hasValidProxyStatus && isRecord(value)) {
      return {
        ...value,
        __apiResponseStatusCode: proxyStatusCode,
      };
    }

    return value;
  };

  const { body } = parsed;

  if (typeof body === 'string') {
    const nestedBody = parseJsonValue(body);
    return nestedBody !== null ? attachProxyStatusCode(nestedBody) : null;
  }

  if (isRecord(body) || Array.isArray(body)) {
    return attachProxyStatusCode(body);
  }

  return null;
}

export async function parseApiResponse(response: Response) {
  const responseText = await response.text();
  if (!responseText) {
    return {};
  }

  if (response.headers.get('content-type')?.toLowerCase().includes('text/html')) {
    return { error: 'Required API endpoint returned the frontend app instead of JSON.' };
  }

  const parsed = parseJsonValue(responseText);
  if (parsed === null) {
    return { message: sanitizeErrorMessage(responseText, '') };
  }

  if (isRecord(parsed)) {
    const unwrappedBody = unwrapProxyApiBody(parsed);
    if (unwrappedBody !== null) {
      return unwrappedBody;
    }
  }

  return parsed;
}

export function getApiResponseStatusCode(response: Response, payload: unknown) {
  if (isRecord(payload)) {
    const proxyStatusCode = Number(payload.__apiResponseStatusCode);
    if (Number.isInteger(proxyStatusCode) && proxyStatusCode >= 100 && proxyStatusCode <= 599) {
      return proxyStatusCode;
    }
  }

  return response.status;
}

export function isApiResponseOk(response: Response, payload: unknown) {
  const statusCode = getApiResponseStatusCode(response, payload);
  return response.ok && statusCode >= 200 && statusCode < 300;
}

export function getApiErrorMessage(payload: unknown, fallback = 'Something went wrong. Please try again.') {
  if (!isRecord(payload)) {
    return fallback;
  }

  return sanitizeErrorMessage(payload.message ?? payload.error, fallback);
}

export function extractAuthTokenFromPayload(payload: unknown) {
  return extractAuthTokenFromPayloadAtDepth(payload, 0);
}

function extractAuthTokenFromPayloadAtDepth(payload: unknown, depth: number) {
  if (typeof payload === 'string') {
    return sanitizeToken(payload);
  }

  if (!isRecord(payload)) {
    return '';
  }

  const directToken = sanitizeTokenFromRecord(payload);
  if (directToken) {
    return directToken;
  }

  if (depth >= 4) {
    return '';
  }

  // Login responses from API Gateway/proxy layers can wrap auth data more
  // than once (for example { data: { auth: { accessToken } } }). Follow only
  // known response-wrapper keys and keep the depth bounded.
  const wrapperKeys = ['data', 'auth', 'result', 'response', 'body'];
  for (const key of wrapperKeys) {
    const nestedValue = payload[key];
    if (isRecord(nestedValue)) {
      const nestedToken = extractAuthTokenFromPayloadAtDepth(nestedValue, depth + 1);
      if (nestedToken) {
        return nestedToken;
      }
    }
  }

  return '';
}

function sanitizeTokenFromRecord(payload: UnknownRecord) {
  return sanitizeToken(
    payload.token ??
    payload.authToken ??
    payload.access_token ??
    payload.accessToken ??
    payload.id_token ??
    payload.idToken ??
    payload.jwt ??
    payload.jwtToken
  );
}

export function extractAuthTokenFromResponse(response: Pick<Response, 'headers'>, payload?: unknown) {
  const payloadToken = extractAuthTokenFromPayload(payload);
  if (payloadToken) {
    return payloadToken;
  }

  const headerToken = sanitizeToken(
    response.headers.get('Authorization')
    ?? response.headers.get('authorization')
    ?? response.headers.get('X-Authorization')
    ?? response.headers.get('x-authorization')
    ?? response.headers.get('X-Auth-Token')
    ?? response.headers.get('x-auth-token')
  );

  return headerToken;
}

export function extractPasswordResetTokenFromPayload(payload: unknown) {
  if (typeof payload === 'string') {
    return sanitizeToken(payload);
  }

  if (!isRecord(payload)) {
    return '';
  }

  const directToken = sanitizeToken(
    payload.token ??
    payload.resetToken ??
    payload.account_id ??
    payload.accountId
  );
  if (directToken) {
    return directToken;
  }

  const data = isRecord(payload.data) ? payload.data : null;
  if (!data) {
    return '';
  }

  return sanitizeToken(
    data.token ??
    data.resetToken ??
    data.account_id ??
    data.accountId
  );
}

export function extractAuthenticatedUserFromPayload(payload: unknown) {
  if (!isRecord(payload)) {
    return null;
  }

  if (isRecord(payload.user)) {
    return payload.user;
  }

  const data = isRecord(payload.data) ? payload.data : null;
  if (data && isRecord(data.user)) {
    return data.user;
  }

  return null;
}

export function sanitizeRequestOrigin(origin: unknown, host?: unknown, forwardedProto?: unknown) {
  const normalizedOrigin = sanitizeImageUrl(origin);
  if (normalizedOrigin && isSafeHost(host)) {
    try {
      const parsedOrigin = new URL(normalizedOrigin);
      if (parsedOrigin.host === host) {
        return parsedOrigin.origin;
      }
    } catch {
      // Fall back to host-derived origin below.
    }
  }

  if (!isSafeHost(host)) {
    return '';
  }

  const safeProto =
    forwardedProto === 'http' || forwardedProto === 'https'
      ? forwardedProto
      : host.includes('localhost') || host.startsWith('127.0.0.1')
        ? 'http'
        : 'https';

  return `${safeProto}://${host}`;
}

export function validateImageUpload(file: File) {
  const allowedMimeTypes = new Set([
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
  ]);

  if (file.size <= 0) {
    return {
      isValid: false,
      error: 'Please upload a non-empty JPG, PNG, WEBP, or HEIC image.',
    };
  }

  if (!allowedMimeTypes.has(file.type)) {
    return {
      isValid: false,
      error: 'Please upload a JPG, PNG, WEBP, or HEIC image.',
    };
  }

  if (file.size > MAX_IMAGE_UPLOAD_SIZE_BYTES) {
    return {
      isValid: false,
      error: formatImageUploadLimitMessage(MAX_IMAGE_UPLOAD_SIZE_BYTES),
    };
  }

  return { isValid: true, error: '' };
}

export function sanitizeImageUrl(value: unknown, allowBlob = false, allowDataUrl = false) {
  if (typeof value !== 'string') {
    return '';
  }

  const nextValue = value.trim();

  if (allowBlob && nextValue.startsWith('blob:')) {
    return nextValue;
  }

  if (allowDataUrl && nextValue.startsWith('data:image/')) {
    if (/^data:image\/[a-zA-Z0-9.+-]+;base64,[a-zA-Z0-9+/=]+$/i.test(nextValue)) {
      return nextValue;
    }
    return '';
  }

  if (nextValue.startsWith('/')) {
    if (nextValue.startsWith('//')) {
      return '';
    }

    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : 'https://vadauro.com';
      return new URL(nextValue, origin).toString();
    } catch {
      return '';
    }
  }

  try {
    const parsed = new URL(nextValue);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      return '';
    }
    return parsed.toString();
  } catch {
    return '';
  }
}

function sanitizeCartDetails(details: unknown): CartItem['details'] | undefined {
  if (!isRecord(details)) {
    return undefined;
  }

  const sanitizedEntries = Object.entries(details).reduce<[string, string[]][]>((acc, [key, value]) => {
    if (!Array.isArray(value)) {
      return acc;
    }

    const safeKey = sanitizeGenericText(key, 40);
    const safeValues = value
      .filter((entry): entry is string => typeof entry === 'string')
      .map((entry) => sanitizeGenericText(entry, 60))
      .filter(Boolean);

    if (!safeKey || safeValues.length === 0) {
      return acc;
    }

    acc.push([safeKey, safeValues]);
    return acc;
  }, []);

  return sanitizedEntries.length > 0 ? Object.fromEntries(sanitizedEntries) : undefined;
}

export function sanitizeStoredCartItem(value: unknown): CartItem | null {
  if (!isRecord(value)) {
    return null;
  }

  const type = value.type;
  if (type !== 'hairstyle' && type !== 'clothing' && type !== 'nails') {
    return null;
  }

  const price = typeof value.price === 'number' ? value.price : Number(value.price);
  const quantity = typeof value.quantity === 'number' ? value.quantity : Number(value.quantity);
  const title = sanitizeGenericText(String(value.title ?? ''), 100);
  const description = sanitizeGenericText(String(value.description ?? ''), 240);
  const image = sanitizeImageUrl(value.image, true, true);

  if (!title || !description || !image || !Number.isFinite(price) || price <= 0 || !Number.isInteger(quantity) || quantity < 1) {
    return null;
  }

  const remoteCartItemIds = [
    value.cartItemId,
    value.cart_item_id,
    ...(Array.isArray(value.remoteCartItemIds) ? value.remoteCartItemIds : []),
    ...(Array.isArray(value.remote_cart_item_ids) ? value.remote_cart_item_ids : []),
  ]
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => sanitizeGenericText(entry, 80))
    .filter(Boolean);

  return {
    id: sanitizeGenericText(String(value.id ?? ''), 64) || `cart-${Date.now()}`,
    itemId: sanitizeGenericText(String(value.itemId ?? value.item_id ?? ''), 80) || undefined,
    cadFileId: sanitizeGenericText(String(value.cadFileId ?? value.cad_file_id ?? ''), 80) || undefined,
    s3Key: sanitizeGenericText(String(value.s3Key ?? value.s3_key ?? ''), 240) || undefined,
    remoteCartItemIds: remoteCartItemIds.length > 0 ? Array.from(new Set(remoteCartItemIds)) : undefined,
    measurements: normalizeMeasurementsRecord(value.measurements),
    size: normalizeSizeValue(value.size),
    fit: normalizeFitLabel(value.fit),
    color: normalizeColorHex(value.color),
    fabricBlend: normalizeFabricBlendLabel(value.fabricBlend ?? value.fabric_blend),
    material: normalizeMaterialRecord(value.material),
    gender: normalizeGenderLabel(value.gender),
    type,
    title,
    description,
    image,
    price: Number(price.toFixed(2)),
    quantity: Math.min(quantity, 99),
    details: sanitizeCartDetails(value.details),
  };
}

export function sanitizeStoredCartItems(value: unknown): CartItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => sanitizeStoredCartItem(item))
    .filter((item): item is CartItem => item !== null);
}

function sanitizeOrderItem(value: unknown): OrderItem | null {
  if (!isRecord(value)) {
    return null;
  }

  const status = value.status;
  if (
    status !== 'pending' &&
    status !== 'ordered' &&
    status !== 'processing' &&
    status !== 'shipped' &&
    status !== 'delivered' &&
    status !== 'cancelled' &&
    status !== 'refunded'
  ) {
    return null;
  }

  const quantity = typeof value.quantity === 'number' ? value.quantity : Number(value.quantity);
  const unitPrice = typeof value.unitPrice === 'number' ? value.unitPrice : Number(value.unitPrice);
  const type = value.type;

  if (
    (type !== 'hairstyle' && type !== 'clothing' && type !== 'nails') ||
    !Number.isInteger(quantity) ||
    quantity < 1 ||
    !Number.isFinite(unitPrice) ||
    unitPrice <= 0
  ) {
    return null;
  }

  return {
    id: sanitizeGenericText(String(value.id ?? ''), 64) || `item-${Date.now()}`,
    itemId: sanitizeGenericText(String(value.itemId ?? value.item_id ?? ''), 80) || undefined,
    cadFileId: sanitizeGenericText(String(value.cadFileId ?? value.cad_file_id ?? ''), 80) || undefined,
    carrier: sanitizeGenericText(String(value.carrier ?? ''), 80),
    trackingNumber: sanitizeGenericText(String(value.trackingNumber ?? value.tracking_number ?? ''), 80),
    title: sanitizeGenericText(String(value.title ?? ''), 100),
    description: sanitizeGenericText(String(value.description ?? ''), 240),
    image: sanitizeImageUrl(value.image),
    quantity: Math.min(quantity, 99),
    unitPrice: Number(unitPrice.toFixed(2)),
    status,
    type,
    estimatedDeliveryDate: sanitizeOrderTimestamp(value.estimatedDeliveryDate ?? value.estimated_delivery_date),
    deliveryEvents: normalizeDeliveryEvents(value.deliveryEvents ?? value.delivery_events),
  };
}

function sanitizeOrderTimestamp(value: unknown) {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return undefined;
  }

  const timestamp = new Date(value);
  return Number.isNaN(timestamp.getTime()) ? undefined : timestamp.toISOString();
}

export function sanitizeStoredOrders(value: unknown): Order[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<Order[]>((acc, entry) => {
    if (!isRecord(entry)) {
      return acc;
    }

    const items = Array.isArray(entry.items)
      ? entry.items.map((item) => sanitizeOrderItem(item)).filter((item): item is OrderItem => item !== null)
      : [];
    const totalAmount =
      typeof entry.totalAmount === 'number' ? entry.totalAmount : Number(entry.totalAmount);
    const subtotal =
      typeof entry.subtotal === 'number' ? entry.subtotal : Number(entry.subtotal);
    const taxAmount =
      typeof entry.taxAmount === 'number' ? entry.taxAmount : Number(entry.taxAmount);
    const shippingCost =
      typeof entry.shippingCost === 'number' ? entry.shippingCost : Number(entry.shippingCost);
    const createdAt =
      typeof entry.createdAt === 'string' && !Number.isNaN(Date.parse(entry.createdAt))
        ? new Date(entry.createdAt).toISOString()
        : null;
    const updatedAt =
      typeof entry.updatedAt === 'string' && !Number.isNaN(Date.parse(entry.updatedAt))
        ? new Date(entry.updatedAt).toISOString()
        : createdAt;

    if (
      !createdAt ||
      items.length === 0 ||
      items.some((item) => !item.title || !item.description) ||
      !Number.isFinite(totalAmount) ||
      totalAmount <= 0
    ) {
      return acc;
    }

    acc.push({
      id: sanitizeGenericText(String(entry.id ?? ''), 64) || `MML-${Date.now()}`,
      accountId: sanitizeGenericText(String(entry.accountId ?? entry.account_id ?? ''), 80),
      createdAt,
      updatedAt: updatedAt ?? createdAt,
      subtotal: Number.isFinite(subtotal) ? Number(subtotal.toFixed(2)) : Number(totalAmount.toFixed(2)),
      taxAmount: Number.isFinite(taxAmount) ? Number(taxAmount.toFixed(2)) : 0,
      shippingCost: Number.isFinite(shippingCost) ? Number(shippingCost.toFixed(2)) : 0,
      totalAmount: Number(totalAmount.toFixed(2)),
      paymentStatus: sanitizeGenericText(String(entry.paymentStatus ?? entry.payment_status ?? 'pending'), 40) || 'pending',
      paymentIntentId: sanitizeGenericText(String(entry.paymentIntentId ?? entry.payment_intent_id ?? ''), 128),
      items,
    });

    return acc;
  }, []);
}

export function sanitizeCheckoutItem(value: unknown): SanitizedCheckoutItem | null {
  if (!isRecord(value)) {
    return null;
  }

  const title = sanitizeGenericText(String(value.title ?? ''), 100);
  const description = sanitizeGenericText(String(value.description ?? ''), 240);
  const image = sanitizeImageUrl(value.image);
  const price = typeof value.price === 'number' ? value.price : Number(value.price);
  const quantity = typeof value.quantity === 'number' ? value.quantity : Number(value.quantity);

  if (
    !title ||
    !description ||
    !image ||
    !Number.isFinite(price) ||
    price <= 0 ||
    price > 100000 ||
    !Number.isInteger(quantity) ||
    quantity < 1 ||
    quantity > 20
  ) {
    return null;
  }

  return {
    title,
    description,
    image,
    price: Number(price.toFixed(2)),
    quantity,
  };
}

export function sanitizeCheckoutItems(value: unknown): SanitizedCheckoutItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => sanitizeCheckoutItem(item))
    .filter((item): item is SanitizedCheckoutItem => item !== null);
}

export function sanitizeCheckoutCustomer(value: unknown): SanitizedCheckoutCustomer | null {
  if (!isRecord(value)) {
    return null;
  }

  const customer = {
    firstName: sanitizeName(String(value.firstName ?? '')),
    lastName: sanitizeName(String(value.lastName ?? '')),
    email: sanitizeEmail(String(value.email ?? '')),
    phone: sanitizePhone(String(value.phone ?? '')),
    company: sanitizeCompany(String(value.company ?? '')),
    address: sanitizeAddress(String(value.address ?? '')),
    city: sanitizeCity(String(value.city ?? '')),
    stateProvince: sanitizeStateProvince(String(value.stateProvince ?? '')),
    postalCode: sanitizePostalCode(String(value.postalCode ?? '')),
    country: normalizeCountryCode(value.country ?? ''),
  };

  if (
    !customer.firstName ||
    !customer.lastName ||
    !customer.email ||
    !isValidEmail(customer.email) ||
    !customer.phone ||
    customer.phone.replace(/\D/g, '').length < 7 ||
    !customer.address ||
    !customer.city ||
    !customer.stateProvince ||
    !customer.postalCode ||
    !customer.country ||
    !isAllowedCountryCode(customer.country)
  ) {
    return null;
  }

  return customer;
}
